import { useCallback, useRef, useState } from 'react'

import type { LogEntry } from 'shared/services/logger/logger.types'
import type { ResolvedFrame } from 'shared/services/logger/stackResolver'
import { resolveStack } from 'shared/services/logger/stackResolver'

export function useStackResolver() {
  const [resolvedStacks, setResolvedStacks] = useState<
    Map<string, ResolvedFrame[]>
  >(new Map())
  const [resolvingIds, setResolvingIds] = useState<Set<string>>(new Set())
  const resolvingRef = useRef<Set<string>>(new Set())

  const resolveEntryStack = useCallback(async (entry: LogEntry) => {
    const rawStack = entry.error?.stack ?? entry.payload.stack
    if (typeof rawStack !== 'string') return
    if (resolvingRef.current.has(entry.id)) return

    resolvingRef.current.add(entry.id)
    setResolvingIds(prev => new Set([...prev, entry.id]))

    try {
      const frames = await resolveStack(rawStack)
      setResolvedStacks(prev => new Map([...prev, [entry.id, frames]]))
    } finally {
      resolvingRef.current.delete(entry.id)
      setResolvingIds(prev => {
        const next = new Set(prev)
        next.delete(entry.id)
        return next
      })
    }
  }, [])

  return { resolvedStacks, resolvingIds, resolveEntryStack }
}
